import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { ActionCardModel, IWidgetComponentApi, ViewMode, WidgetNode } from '@playbooks/action-cards-utils'
import { ActionCardsConfig, ActionCardsElement } from '@playbooks/action-cards'

import { trackFunnelViewSlideChanged } from '../services/trackService'
import useMountEffect from './useMountEffect'
import { SliderManager, Step, StepType } from '../types'
import { instance as autoLaunchedActionsDbStore } from '../services/autoLaunchedActionsDbStore'

const STEPS_CORRECTION = 1

function useHorizontalSliderMethods({
  viewMode,
  playbookId,
  sliderManagerRef,
  setIsFinishButtonVisible,
  actionCardsElementRef,
  actionCardsConfig,
}: {
  viewMode: ViewMode | undefined
  playbookId: string
  sliderManagerRef: React.RefObject<SliderManager | null>
  setIsFinishButtonVisible: (flag: boolean) => void
  actionCardsElementRef: React.RefObject<ActionCardsElement | null>
  actionCardsConfig: ActionCardsConfig
}) {
  const [currentStep, setCurrentStep] = useState<Step | null>(null)
  const [steps, setSteps] = useState<Step[]>([])
  const scrollContainer = useRef<HTMLDivElement>(null)
  const stepRef = useRef(0)
  const actionsAutoLaunchWasTriggeredOnInitialization = useRef(false)

  const mapScopesToSteps = useCallback(() => {
    if (viewMode !== ViewMode.FUNNEL) return
    if (!scrollContainer.current) return

    let stepCounter = 0
    // to ensure that slides are loaded and we can get the correct number of them
    const scopes = [...scrollContainer.current!.querySelectorAll('[data-separator-scope]')]

    const mappedSteps = scopes.map(x => {
      return {
        type: stepCounter === 0 ? StepType.FIRST : null,
        step: stepCounter++,
        stepsNumber: scopes.length,
        separatorId: Number.parseInt(x.getAttribute('data-separator-scope') || '0') || 0,
      }
    })

    mappedSteps.push({
      type: StepType.LAST,
      step: mappedSteps.length,
      stepsNumber: mappedSteps.length,
      separatorId: 0,
    })

    setSteps(mappedSteps)
    return mappedSteps
  }, [viewMode])

  useMountEffect(() => {
    const mappedSteps = mapScopesToSteps()
    if (!mappedSteps) {
      return
    }
    trackFunnelViewSlideChanged(playbookId, mappedSteps[0].separatorId)
  })

  /**
   * Trigger autoLaunchActions when serviceAuthToken becomes available.
   * This effect runs once during initialization when the authentication token is set.
   * It automatically launches all actions marked for auto-launch in the first separator scope.
   * Uses a ref flag to ensure autoLaunchActions is only triggered once on component initialization.
   *
   * Dependencies are required by exhaustive-deps ESLint rule since all values are used in the effect.
   * The ref flag prevents re-execution even if dependencies change, making this safe.
   */
  useEffect(() => {
    if (actionsAutoLaunchWasTriggeredOnInitialization.current) {
      return
    }
    if (!actionCardsConfig.serviceAuthToken) {
      return
    }
    const mappedSteps = mapScopesToSteps()
    if (!mappedSteps) {
      return
    }
    actionsAutoLaunchWasTriggeredOnInitialization.current = true
    autoLaunchActions(playbookId, actionCardsElementRef, mappedSteps[0].separatorId)
  }, [actionCardsConfig.serviceAuthToken, actionCardsElementRef, mapScopesToSteps, playbookId])

  useImperativeHandle(sliderManagerRef, () => {
    return {
      nextSlide: () => {
        const steps = mapScopesToSteps()
        if (!steps) return

        const stepSize = scrollContainer.current?.clientWidth || 0

        scrollContainer.current?.scrollTo({
          left: stepSize * (stepRef.current + STEPS_CORRECTION),
          behavior: 'smooth',
        })

        if (steps[stepRef.current].type !== StepType.LAST) stepRef.current++

        const currentStepNumber = steps[stepRef.current]

        trackFunnelViewSlideChanged(playbookId, currentStepNumber.separatorId)
        autoLaunchActions(playbookId, actionCardsElementRef, currentStepNumber.separatorId)
        setIsFinishButtonVisible(steps[stepRef.current].type === StepType.LAST)
        setCurrentStep(currentStepNumber)

        return currentStepNumber
      },
      prevSlide: () => {
        const stepSize = scrollContainer.current?.clientWidth || 0

        scrollContainer.current?.scrollTo({
          left: stepSize * (stepRef.current - STEPS_CORRECTION),
          behavior: 'smooth',
        })

        if (steps[stepRef.current].type !== StepType.FIRST) stepRef.current--

        const currentStepNumber = steps[stepRef.current]

        autoLaunchActions(playbookId, actionCardsElementRef, currentStepNumber.separatorId)
        setIsFinishButtonVisible(currentStepNumber.type === StepType.LAST)
        setCurrentStep(currentStepNumber)
        return currentStepNumber
      },
      getCurrentStep: () => {
        return steps[stepRef.current]
      },
    }
  })

  return { scrollContainer, step: currentStep }
}

/**
 * Automatically trigger all actions within the provided separator scope
 * that have been marked for auto-launch and have not been auto-launched yet
 */
async function autoLaunchActions(
  playbookId: string,
  actionCardsElementRef: React.RefObject<ActionCardsElement | null>,
  separatorId: number,
) {
  const actionCardData = actionCardsElementRef.current?.getValue()
  if (separatorId > 0 && actionCardData) {
    const autoLaunchedActionIds = (await autoLaunchedActionsDbStore.get(playbookId)) ?? []

    const activationPromises: Promise<void>[] = []
    new ActionCardModel(actionCardData)
      .getSearchService()
      .filterVisible()
      .filterByAncestor(separatorId)
      .filter(
        x =>
          x instanceof WidgetNode &&
          (x as WidgetNode).autoLaunch &&
          (!autoLaunchedActionIds || autoLaunchedActionIds.indexOf(x.getId()) === -1),
      )
      .forEach(x => {
        const widgetId = x.getId()
        const widgetApi = actionCardsElementRef.current?.componentApiProvider?.get(widgetId) as IWidgetComponentApi
        const promise = widgetApi?.activate()

        if (promise) {
          activationPromises.push(
            promise.then(() => {
              autoLaunchedActionIds.push(widgetId)
            }),
          )
        }
      })

    if (activationPromises.length > 0) {
      await Promise.all(activationPromises)
      await autoLaunchedActionsDbStore.save(playbookId, autoLaunchedActionIds)
    }
  }
}

export default useHorizontalSliderMethods
